import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import { Link } from "react-router-dom";
import TaskSkeleton from "./TaskSkeleton";
import Select from "react-select";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const compactStyles = `
    .head-detail-table { font-size: 0.8rem; }
    .head-detail-table thead th { font-size: 0.75rem; padding: 8px 6px; white-space: nowrap; }
    .head-detail-table tbody td { font-size: 0.75rem; padding: 6px 5px; white-space: nowrap; }
`;

export default function FeesHeadDetails() {
    const { http, user } = AuthUser();
    const [head1Data, setHead1Data] = useState([]);
    const [head2Data, setHead2Data] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const [stClass, setStClass] = useState([]);
    const [stDev, setStDev] = useState([]);
    const [stYear, setStYear] = useState([]);
    const [stAdmin, setStAdmin] = useState([]);
    const [stAgent, setStAgent] = useState([]);
    const [fees, setFees] = useState([]);
    const [feesTypeHeads, setFeesTypeHeads] = useState([]);
    const [feesTypeHeads2, setFeesTypeHeads2] = useState([]);

    const [selectedFrom, setSelectedFrom] = useState(moment().format("YYYY-MM-DD HH:mm:ss"));
    const [selectedTo, setSelectedTo] = useState(moment().format("YYYY-MM-DD HH:mm:ss"));
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [selectedClassStud, setSelectedClassStud] = useState([]);
    const [selectedDevStud, setSelectedDevStud] = useState("");
    const [selectedDevStudType, setSelectedDevStudType] = useState([]);
    const [selectedDAdmin, setSelectedDAdmin] = useState("");
    const [selectedDevStudAgentType, setSelectedDevStudAgentType] = useState("");
    const [selectedHead, setSelectedHead] = useState([]);
    const [selectedHead2, setSelectedHead2] = useState([]);
    const [selectedDevYear, setSelectedDevYear] = useState("");

    useEffect(() => {
        http.get(`/class?company_id=${user.company_id}`).then((res) => setStClass(res.data.data));
        http.get(`/devision`).then((res) => setStDev(res.data.data));
        http.get(`/admins?login_user=${user.id}`).then((res) => setStAdmin(res.data.users));
        http.get(`/year`).then((res) => setStYear(res.data.data));
        http.get(`/agent?company_id=${user.company_id}`).then((res) => setStAgent(res.data.data));
        http.get(`/fees_type_heads?login_user=${user.id}`).then((res) => { if (res.data.success) setFeesTypeHeads(res.data.data || []); });
        http.get(`/fees_type_heads_2?login_user=${user.id}`).then((res) => { if (res.data.success) setFeesTypeHeads2(res.data.data || []); });
        fetchData();
    }, []);

    const handleSelectChangeYear = (event) => {
        setSelectedDevYear(event.target.value);
        http.get(`/tution_feeslists?company_id=${user.company_id}&year=${event.target.value}`)
            .then((res) => setFees(res.data.data))
            .catch(() => {});
    };

    const fetchData = (
        fromDate = selectedFrom, toDate = selectedTo, selClass = selectedClassStud,
        selDev = selectedDevStud, typePays = selectedDevStudType, byAdmin = selectedDAdmin,
        agent = selectedDevStudAgentType, createdFromDate = createdFrom, createdToDate = createdTo,
        headFilter = selectedHead, head2Filter = selectedHead2
    ) => {
        setIsLoading(true);
        const classParam = Array.isArray(selClass) ? selClass.map(o => o.value).join(",") : selClass;
        const typeParam = Array.isArray(typePays) ? typePays.map(o => o.value).join(",") : typePays;
        const headParam = Array.isArray(headFilter) ? headFilter.map(o => o.value).join(",") : headFilter;
        const head2Param = Array.isArray(head2Filter) ? head2Filter.map(o => o.value).join(",") : head2Filter;

        const endpoint = `/fees_head_details?date_from=${fromDate}&date_to=${toDate}&class=${classParam}&dev=${selDev}&type=${typeParam}&added=${byAdmin}&company_id=${user.company_id}&agent=${agent}&created_from=${createdFromDate}&created_to=${createdToDate}&head=${headParam}&head2=${head2Param}`;

        http.get(endpoint)
            .then((res) => {
                setIsLoading(false);
                setHead1Data(res.data.head1 || []);
                setHead2Data(res.data.head2 || []);
                setTotalRecords(res.data.total_records || 0);
            })
            .catch(() => {
                setIsLoading(false);
                toast.error("Error fetching data");
            });
    };

    const handleFilter = () => {
        fetchData(selectedFrom, selectedTo, selectedClassStud, selectedDevStud, selectedDevStudType, selectedDAdmin, selectedDevStudAgentType, createdFrom, createdTo, selectedHead, selectedHead2);
    };

    const handleClear = () => window.location.reload();

    const handleMonthChange = (event) => {
        const month = event.target.value;
        if (!month) return;
        const year = moment().year();
        const start = moment(`${year}-${month}-01`).startOf("month").format("YYYY-MM-DD");
        const end = moment(`${year}-${month}-01`).endOf("month").format("YYYY-MM-DD");
        setSelectedFrom(start);
        setSelectedTo(end);
    };

    const fmt = (v) => parseFloat(v || 0).toFixed(2);

    const exclConcTotal = (row) => parseFloat(row.total || 0) - parseFloat(row.concession || 0);

    const calcTotals = (data) => data.reduce((acc, r) => ({
        concession: acc.concession + r.concession,
        cash: acc.cash + r.cash,
        upi: acc.upi + r.upi,
        dbank: acc.dbank + r.dbank,
        agent: acc.agent + r.agent,
        total: acc.total + r.total,
        exclConc: acc.exclConc + exclConcTotal(r),
        count: acc.count + r.count,
    }), { concession: 0, cash: 0, upi: 0, dbank: 0, agent: 0, total: 0, exclConc: 0, count: 0 });

    const renderTable = (data, title) => {
        const totals = calcTotals(data);
        return (
            <div className="mb-4">
                <h6 className="fw-bold text-primary mb-2"><i className="fas fa-layer-group me-1"></i>{title} <span className="badge bg-secondary ms-1">{data.length}</span></h6>
                <div className="table-responsive">
                    <table className="table table-striped table-hover head-detail-table">
                        <thead className="table-primary">
                            <tr>
                                <th style={{width:'40px'}}>#</th>
                                <th>{title}</th>
                                <th style={{width:'60px'}}>Count</th>
                                <th style={{minWidth:'80px'}}>Concession</th>
                                <th style={{minWidth:'80px'}}>Cash</th>
                                <th style={{minWidth:'80px'}}>UPI</th>
                                <th style={{minWidth:'80px'}}>Dbank</th>
                                <th style={{minWidth:'80px'}}>Mode</th>
                                <th style={{minWidth:'90px'}}>Total</th>
                                <th style={{minWidth:'110px'}}>Conc. Excl. Total</th>
                            </tr>
                            <tr className="table-warning">
                                <th></th>
                                <th><strong>Total</strong></th>
                                <th><strong>{totals.count}</strong></th>
                                <th><strong style={{color:'red'}}>({fmt(totals.concession)})</strong></th>
                                <th><strong>{fmt(totals.cash)}</strong></th>
                                <th><strong>{fmt(totals.upi)}</strong></th>
                                <th><strong>{fmt(totals.dbank)}</strong></th>
                                <th><strong>{fmt(totals.agent)}</strong></th>
                                <th><strong>{fmt(totals.total)}</strong></th>
                                <th><strong>{fmt(totals.exclConc)}</strong></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr><td colSpan={10} className="text-center text-muted py-3">No data</td></tr>
                            ) : (
                                data.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{idx + 1}</td>
                                        <td><strong>{row.head}</strong></td>
                                        <td>{row.count}</td>
                                        <td><span style={{color:'red'}}>{row.concession !== 0 ? fmt(row.concession) : ''}</span></td>
                                        <td>{row.cash !== 0 ? fmt(row.cash) : ''}</td>
                                        <td>{row.upi !== 0 ? fmt(row.upi) : ''}</td>
                                        <td>{row.dbank !== 0 ? fmt(row.dbank) : ''}</td>
                                        <td>{row.agent !== 0 ? fmt(row.agent) : ''}</td>
                                        <td><strong>{fmt(row.total)}</strong></td>
                                        <td><strong>{fmt(exclConcTotal(row))}</strong></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const buildExcelSheet = (data, title) => {
        const headerStyle = { font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 }, fill: { fgColor: { rgb: "1565C0" } }, alignment: { horizontal: "center" }, border: { bottom: { style: "thin", color: { rgb: "000000" } } } };
        const totalStyle = { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: "FFF9C4" } }, border: { bottom: { style: "thin", color: { rgb: "000000" } } } };
        const numStyle = { numFmt: "#,##0.00" };

        const headers = ["#", title, "Count", "Concession", "Cash", "UPI", "Dbank", "Mode", "Total", "Conc. Excl. Total"];
        const rows = data.map((r, i) => [i + 1, r.head, r.count, r.concession, r.cash, r.upi, r.dbank, r.agent, r.total, exclConcTotal(r)]);
        const totals = calcTotals(data);
        rows.push(["", "Total", totals.count, totals.concession, totals.cash, totals.upi, totals.dbank, totals.agent, totals.total, totals.exclConc]);

        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        headers.forEach((_, ci) => {
            const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
            if (ws[ref]) ws[ref].s = headerStyle;
        });
        const lastRow = wsData.length - 1;
        headers.forEach((_, ci) => {
            const ref = XLSX.utils.encode_cell({ r: lastRow, c: ci });
            if (ws[ref]) ws[ref].s = totalStyle;
        });
        for (let ri = 1; ri <= rows.length; ri++) {
            for (let ci = 3; ci <= 9; ci++) {
                const ref = XLSX.utils.encode_cell({ r: ri, c: ci });
                if (ws[ref] && typeof ws[ref].v === "number") ws[ref].s = { ...ws[ref].s, ...numStyle };
            }
        }
        ws["!cols"] = [{ wch: 5 }, { wch: 25 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 16 }];
        return ws;
    };

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();
        if (head1Data.length > 0) XLSX.utils.book_append_sheet(wb, buildExcelSheet(head1Data, "Head 1"), "Head 1");
        if (head2Data.length > 0) XLSX.utils.book_append_sheet(wb, buildExcelSheet(head2Data, "Head 2"), "Head 2");
        if (wb.SheetNames.length === 0) { toast.error("No data to export"); return; }
        XLSX.writeFile(wb, `Fees_Head_Details_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
    };

    const buildPdfTable = (doc, data, title, startY) => {
        const totals = calcTotals(data);
        const headers = [["#", title, "Count", "Concession", "Cash", "UPI", "Dbank", "Mode", "Total", "Conc. Excl. Total"]];
        const rows = data.map((r, i) => [i + 1, r.head, r.count, fmt(r.concession), fmt(r.cash), fmt(r.upi), fmt(r.dbank), fmt(r.agent), fmt(r.total), fmt(exclConcTotal(r))]);
        rows.push(["", "Total", totals.count, fmt(totals.concession), fmt(totals.cash), fmt(totals.upi), fmt(totals.dbank), fmt(totals.agent), fmt(totals.total), fmt(totals.exclConc)]);

        autoTable(doc, {
            head: headers,
            body: rows,
            startY,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: "bold" },
            didParseCell: (data) => {
                if (data.row.index === rows.length - 1 && data.section === "body") {
                    data.cell.styles.fillColor = [255, 249, 196];
                    data.cell.styles.fontStyle = "bold";
                }
            },
        });
        return doc.lastAutoTable.finalY;
    };

    const handleExportPdf = () => {
        if (head1Data.length === 0 && head2Data.length === 0) { toast.error("No data to export"); return; }
        const doc = new jsPDF("l", "mm", "a4");
        doc.setFontSize(14);
        doc.text("Fees Head Details", 14, 15);
        doc.setFontSize(9);
        doc.text(`Date: ${moment(selectedFrom).format("DD MMM YYYY")} - ${moment(selectedTo).format("DD MMM YYYY")}  |  Total Records: ${totalRecords}`, 14, 22);

        let y = 28;
        if (head1Data.length > 0) {
            doc.setFontSize(11);
            doc.text("Grouped by Head 1", 14, y);
            y = buildPdfTable(doc, head1Data, "Head 1", y + 3);
            y += 8;
        }
        if (head2Data.length > 0) {
            if (y > 170) { doc.addPage(); y = 15; }
            doc.setFontSize(11);
            doc.text("Grouped by Head 2", 14, y);
            buildPdfTable(doc, head2Data, "Head 2", y + 3);
        }
        doc.save(`Fees_Head_Details_${moment().format("YYYYMMDD_HHmm")}.pdf`);
    };

    const selectSmall = {
        control: (base) => ({ ...base, minHeight: "31px", fontSize: "12px" }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        placeholder: (base) => ({ ...base, fontSize: "12px" }),
        valueContainer: (base) => ({ ...base, padding: "0 6px" }),
        dropdownIndicator: (base) => ({ ...base, padding: "2px" }),
        clearIndicator: (base) => ({ ...base, padding: "2px" }),
    };

    const multiBlue = { ...selectSmall, multiValue: (base) => ({ ...base, backgroundColor: "#e3f2fd", borderRadius: "3px" }), multiValueLabel: (base) => ({ ...base, fontSize: "11px", padding: "0 3px" }), multiValueRemove: (base) => ({ ...base, ":hover": { backgroundColor: "#bbdefb", color: "#1565c0" } }) };
    const multiOrange = { ...selectSmall, multiValue: (base) => ({ ...base, backgroundColor: "#fff3e0", borderRadius: "3px" }), multiValueLabel: (base) => ({ ...base, fontSize: "11px", padding: "0 3px" }), multiValueRemove: (base) => ({ ...base, ":hover": { backgroundColor: "#ffe0b2", color: "#e65100" } }) };
    const multiGreen = { ...selectSmall, multiValue: (base) => ({ ...base, backgroundColor: "#e8f5e9", borderRadius: "3px" }), multiValueLabel: (base) => ({ ...base, fontSize: "11px", padding: "0 3px" }), multiValueRemove: (base) => ({ ...base, ":hover": { backgroundColor: "#c8e6c9", color: "#2e7d32" } }) };
    const multiPurple = { ...selectSmall, multiValue: (base) => ({ ...base, backgroundColor: "#f3e5f5", borderRadius: "3px" }), multiValueLabel: (base) => ({ ...base, fontSize: "11px", padding: "0 3px" }), multiValueRemove: (base) => ({ ...base, ":hover": { backgroundColor: "#e1bee7", color: "#6a1b9a" } }) };

    return (
        <>
            <style>{compactStyles}</style>
            <Toaster />
            <div className="container-fluid mt-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div>
                        <h5 className="mb-0 fw-bold">Fees Head Details</h5>
                        <small style={{ fontSize: "0.7rem" }} className="text-muted">Grouped by Head 1 &amp; Head 2 &nbsp;|&nbsp; Total Records: <strong>{totalRecords}</strong></small>
                    </div>
                    <div className="d-flex gap-1 flex-wrap">
                        <Link to="/paidlist" className="btn btn-outline-secondary btn-sm px-2 py-0" style={{ fontSize: "0.75rem" }}>
                            <i className="fas fa-arrow-left me-1"></i>Daily Report
                        </Link>
                        <button className="btn btn-outline-secondary btn-sm px-2 py-0" style={{ fontSize: "0.75rem" }} onClick={handleClear}>Clear</button>
                        <button className="btn btn-outline-success btn-sm px-2 py-0" style={{ fontSize: "0.75rem" }} onClick={handleExportExcel} disabled={head1Data.length === 0 && head2Data.length === 0}>
                            <i className="fas fa-file-excel me-1"></i>Excel
                        </button>
                        <button className="btn btn-outline-danger btn-sm px-2 py-0" style={{ fontSize: "0.75rem" }} onClick={handleExportPdf} disabled={head1Data.length === 0 && head2Data.length === 0}>
                            <i className="fas fa-file-pdf me-1"></i>PDF
                        </button>
                    </div>
                </div>

                {/* Filters Row 1 */}
                <div className="row g-2 align-items-center">
                    <div className="col-auto">
                        <select onChange={handleMonthChange} className="form-control form-control-sm border border-warning border-2" style={{fontSize: '12px', minWidth: '90px'}} title="Select Month">
                            <option value="">Month</option>
                            <option value="01">January</option>
                            <option value="02">February</option>
                            <option value="03">March</option>
                            <option value="04">April</option>
                            <option value="05">May</option>
                            <option value="06">June</option>
                            <option value="07">July</option>
                            <option value="08">August</option>
                            <option value="09">September</option>
                            <option value="10">October</option>
                            <option value="11">November</option>
                            <option value="12">December</option>
                        </select>
                    </div>
                    <div className="col">
                        <input type="date" className="form-control form-control-sm border border-success border-2" value={selectedFrom} onChange={(e) => setSelectedFrom(e.target.value)} title="Paid Date From" />
                    </div>
                    <div className="col">
                        <input type="date" className="form-control form-control-sm border border-success border-2" value={selectedTo} onChange={(e) => setSelectedTo(e.target.value)} title="Paid Date To" />
                    </div>
                    <div className="col">
                        <Select isMulti options={stClass.map((o) => ({ value: o.class_name, label: o.class_name }))} onChange={(s) => setSelectedClassStud(s || [])} value={selectedClassStud} isClearable placeholder="Class" menuPlacement="auto" menuPosition="absolute" styles={multiBlue} />
                    </div>
                    <div className="col">
                        <Select isMulti options={fees.map((o) => ({ value: o.id, label: o.name }))} onChange={(s) => setSelectedDevStudType(s || [])} value={selectedDevStudType} isClearable placeholder="Fees Type" menuPlacement="auto" menuPosition="absolute" styles={multiOrange} />
                    </div>
                    <div className="col">
                        <select onChange={(e) => setSelectedDevStud(e.target.value)} className="form-control form-control-sm"><option value="">Div</option>{stDev.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}</select>
                    </div>
                    <div className="col">
                        <select onChange={handleSelectChangeYear} className="form-control form-control-sm"><option value="">Year</option>{stYear.map((o, i) => <option key={i} value={o.name}>{o.name}</option>)}</select>
                    </div>
                    <div className="col">
                        <Select options={[{ value: "", label: "Mode" }, ...stAgent.map((o) => ({ value: o.id, label: o.name }))]} onChange={(s) => setSelectedDevStudAgentType(s ? s.value : "")} value={stAgent.find(o => o.id === selectedDevStudAgentType) ? { value: selectedDevStudAgentType, label: stAgent.find(o => o.id === selectedDevStudAgentType).name } : { value: "", label: "Mode" }} isClearable placeholder="Mode" menuPlacement="auto" menuPosition="absolute" styles={selectSmall} />
                    </div>
                </div>

                {/* Filters Row 2 */}
                <div className="row g-2 align-items-center mt-1">
                    <div className="col-auto" style={{ minWidth: "170px" }}>
                        <Select isMulti options={feesTypeHeads.map((h) => ({ value: h.id, label: h.name }))} onChange={(s) => setSelectedHead(s || [])} value={selectedHead} isClearable placeholder="Head 1" menuPlacement="auto" menuPosition="absolute" styles={multiGreen} />
                    </div>
                    <div className="col-auto" style={{ minWidth: "170px" }}>
                        <Select isMulti options={feesTypeHeads2.map((h) => ({ value: h.id, label: h.name }))} onChange={(s) => setSelectedHead2(s || [])} value={selectedHead2} isClearable placeholder="Head 2" menuPlacement="auto" menuPosition="absolute" styles={multiPurple} />
                    </div>
                    <div className="col-auto">
                        <select onChange={(e) => setSelectedDAdmin(e.target.value)} className="form-control form-control-sm" style={{ fontSize: "12px" }}><option value="">Added By</option>{stAdmin.map((o, i) => <option key={i} value={o.id}>{o.name} {o.last_name}</option>)}</select>
                    </div>
                    <div className="col-auto">
                        <input type="date" className="form-control form-control-sm border border-info" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} title="Created From" style={{ fontSize: "12px" }} />
                    </div>
                    <div className="col-auto">
                        <input type="date" className="form-control form-control-sm border border-info" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} title="Created To" style={{ fontSize: "12px" }} />
                    </div>
                    <div className="col-auto">
                        <button className="btn btn-primary btn-sm px-3" onClick={handleFilter}>
                            <i className="fas fa-search me-1"></i>Search
                        </button>
                    </div>
                </div>

                <hr />

                {isLoading ? (
                    <TaskSkeleton />
                ) : (
                    <>
                        {renderTable(head1Data, "Head 1")}
                        {renderTable(head2Data, "Head 2")}
                    </>
                )}
            </div>
        </>
    );
}
